import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, useColorModeValue } from '@ecoinvent/ui';
import { ReactFlow, useReactFlow } from '@xyflow/react';
import { BiCollapse } from 'react-icons/bi';

import useV4LciaScores from 'hooks/data/datasets/V4/lcia/useV4LciaScores';
import useV4NodeScores from 'hooks/data/datasets/V4/lcia/useV4NodeScores';
import { SupplyChainNode } from 'hooks/data/datasets/V4/supplyChain/types';
import { DerivedEdge, NodeCounts, ShownCounts } from 'hooks/data/datasets/V4/supplyChain/useSupplyChain';
import { useGraphDownload } from 'hooks/useGraphDownload';
import GraphControls from 'pages/Flows/FlowsView/FlowGraphView/GraphControls';

import { COLORS, DIM_OPACITY, SUPPLY_GRAPH_HEIGHT } from './constants';
import { formatLciaUnit } from './formatLciaUnit';
import OverflowNodeComponent from './OverflowNode';
import SupplyChainEdgeComponent, { SupplyChainEdgeData } from './SupplyChainEdge';
import { buildAdjacency, computeHighlight, HoverTarget } from './supplyChainHighlight';
import SupplyChainInfoCard from './SupplyChainInfoCard';
import SupplyChainLciaFilters, { LciaSelection } from './SupplyChainLciaFilters';
import SupplyChainLegend from './SupplyChainLegend';
import SupplyChainNodeComponent from './SupplyChainNode';
import { useSupplyChainLayout } from './useSupplyChainLayout';

const nodeTypes = {
  supplyChainNode: SupplyChainNodeComponent,
  supplyChainOverflow: OverflowNodeComponent,
};
const edgeTypes = { supplyChainEdge: SupplyChainEdgeComponent };
// Forwarded to d3-zoom's clickDistance, which decides when a gesture pans, so do not widen it.
const PANE_CLICK_DISTANCE = 1;
const DRAG_THRESHOLD = 4;

type Props = {
  nodes: SupplyChainNode[];
  edges: DerivedEdge[];
  nodeCounts: NodeCounts;
  shownCounts: ShownCounts;
  rootNodeId: string;
  expandedNodeIds: Set<string>;
  expandedOutputNodeIds: Set<string>;
  isFullScreen: boolean;
  mode: 'flows' | 'impact_assessment';
  selectedMethodId: string;
  lciaSelection: LciaSelection | null;
  onLciaChange: (selection: LciaSelection) => void;
  onExpand: (id: string) => void;
  onCollapse: (id: string, activeNodeId?: string | null) => void;
  onExpandOutputs: (id: string) => void;
  onCollapseOutputs: (id: string, activeNodeId?: string | null) => void;
  onCollapseAll: () => void;
  onViewDataset: (id: string) => void;
  onToggleFullScreen: () => void;
  showMoreInputs: (id: string) => void;
  showMoreOutputs: (id: string) => void;
  isInfoCardOpen: boolean;
  infoStepIndex: number;
  onInfoCardNext: () => void;
  onInfoCardClose: () => void;
};

const SupplyChainCanvas = ({
  nodes,
  edges,
  nodeCounts,
  shownCounts,
  rootNodeId,
  expandedNodeIds,
  expandedOutputNodeIds,
  isFullScreen,
  mode,
  selectedMethodId,
  lciaSelection,
  onLciaChange,
  onExpand,
  onCollapse,
  onExpandOutputs,
  onCollapseOutputs,
  onCollapseAll,
  onViewDataset,
  onToggleFullScreen,
  showMoreInputs,
  showMoreOutputs,
  isInfoCardOpen,
  infoStepIndex,
  onInfoCardNext,
  onInfoCardClose,
}: Props) => {
  const selectedIndicatorId = lciaSelection?.indicatorId ?? '';
  const isImpactMode = mode === 'impact_assessment';

  const scoreNodeIds = useMemo(() => nodes.map((n) => n.datasetId ?? n.id), [nodes]);
  const {
    scoreByNodeId,
    isLoading: scoresLoading,
    isError: scoresError,
  } = useV4NodeScores(scoreNodeIds, isImpactMode ? selectedIndicatorId : '');

  const { data: rootScores } = useV4LciaScores(rootNodeId, isImpactMode ? selectedMethodId : '');
  const scoreUnit = useMemo(() => {
    const row = rootScores?.find((s) => s.id === selectedIndicatorId);
    return row ? formatLciaUnit(row.unit) : '';
  }, [rootScores, selectedIndicatorId]);

  const onCopyScore = useCallback(
    async (nodeId: string) => {
      const score = scoreByNodeId.get(nodeId.split('#')[0]);
      if (typeof score === 'number') await navigator.clipboard.writeText(`${score}`);
    },
    [scoreByNodeId]
  );

  const bgColor = useColorModeValue('#F6F8FD', 'gray.800');
  const buttonBg = useColorModeValue('white', COLORS.darkSurface);
  const buttonColor = useColorModeValue('gray.700', 'gray.100');
  const buttonHoverBg = useColorModeValue('gray.50', 'gray.600');
  const buttonBorderColor = useColorModeValue(COLORS.borderLight, COLORS.buttonOutlinedBorderDark);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<HoverTarget | null>(null);
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null);
  const activeNodeIdRef = useRef<string | null>(null);
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null);
  const draggedRef = useRef(false);
  const { fitView } = useReactFlow();

  // Sole writer of the selection, so the ref read at click time cannot drift.
  const selectNode = useCallback((id: string | null) => {
    activeNodeIdRef.current = id;
    setActiveNodeId((prev) => (prev === id ? prev : id));
  }, []);

  useEffect(() => {
    if (activeNodeId && !nodes.some((n) => n.id === activeNodeId)) selectNode(null);
  }, [nodes, activeNodeId, selectNode]);

  const hoverEnter = useCallback((kind: HoverTarget['kind'], id: string) => {
    setHovered((prev) => (prev?.kind === kind && prev.id === id ? prev : { kind, id }));
  }, []);
  // Id-guarded: the outgoing element's leave must not clear the target the pointer just entered.
  const hoverLeave = useCallback((kind: HoverTarget['kind'], id: string) => {
    setHovered((prev) => (prev?.kind === kind && prev.id === id ? null : prev));
  }, []);

  const handleEdgeHover = useCallback(
    (id: string, isHovered: boolean) => {
      if (isHovered) hoverEnter('edge', id);
      else hoverLeave('edge', id);
    },
    [hoverEnter, hoverLeave]
  );

  const recordPointerDown = useCallback((e: MouseEvent) => {
    pointerDownRef.current = { x: e.clientX, y: e.clientY };
    draggedRef.current = false;
  }, []);

  const trackDrag = useCallback((e: MouseEvent) => {
    const down = pointerDownRef.current;
    if (draggedRef.current || !down) return;
    if (Math.hypot(e.clientX - down.x, e.clientY - down.y) > DRAG_THRESHOLD) draggedRef.current = true;
  }, []);

  // A drag off a `nopan` edge still ends in a pane click; deselecting there would lose the selection.
  const handleDeselect = useCallback(() => {
    if (draggedRef.current) return;
    selectNode(null);
  }, [selectNode]);

  const handleExpand = useCallback(
    (id: string) => {
      selectNode(id);
      onExpand(id);
    },
    [onExpand, selectNode]
  );
  const handleExpandOutputs = useCallback(
    (id: string) => {
      selectNode(id);
      onExpandOutputs(id);
    },
    [onExpandOutputs, selectNode]
  );
  const handleCollapse = useCallback((id: string) => onCollapse(id, activeNodeIdRef.current), [onCollapse]);
  const handleCollapseOutputs = useCallback(
    (id: string) => onCollapseOutputs(id, activeNodeIdRef.current),
    [onCollapseOutputs]
  );

  const handleSelectNode = useCallback((id: string) => selectNode(id), [selectNode]);

  const { nodes: flowNodes, edges: flowEdges } = useSupplyChainLayout({
    nodes,
    edges,
    nodeCounts,
    shownCounts,
    rootNodeId,
    expandedNodeIds,
    expandedOutputNodeIds,
    activeNodeId,
    mode,
    selectedIndicatorId,
    scoreByNodeId,
    scoreUnit,
    scoresLoading,
    scoresError,
    onExpand: handleExpand,
    onCollapse: handleCollapse,
    onExpandOutputs: handleExpandOutputs,
    onCollapseOutputs: handleCollapseOutputs,
    onSelectNode: handleSelectNode,
    onViewDataset,
    onCopyScore,
    showMoreInputs,
    showMoreOutputs,
  });

  const handleDownload = useGraphDownload(containerRef, controlsRef, 'supply-chain.png');

  const adjacency = useMemo(() => buildAdjacency(flowEdges), [flowEdges]);
  const nodeIds = useMemo(() => new Set(flowNodes.map((n) => n.id)), [flowNodes]);

  // An element can unmount under the pointer (show more), and a stale target would dim everything.
  const highlight = useMemo(() => {
    if (!hovered) return null;
    const exists = hovered.kind === 'node' ? nodeIds.has(hovered.id) : adjacency.endpoints.has(hovered.id);
    return exists ? computeHighlight(adjacency, hovered) : null;
  }, [adjacency, nodeIds, hovered]);

  const displayNodes = useMemo(() => {
    if (!highlight) return flowNodes;
    return flowNodes.map((n) => (highlight.nodeIds.has(n.id) ? n : { ...n, style: { ...n.style, opacity: DIM_OPACITY } }));
  }, [flowNodes, highlight]);

  const displayEdges = useMemo(
    () =>
      flowEdges.map((e) => {
        const dimmed = highlight !== null && !highlight.edgeIds.has(e.id);
        const data: SupplyChainEdgeData = {
          ...(e.data as SupplyChainEdgeData | undefined),
          dimmed,
          hovered: hovered?.kind === 'edge' && hovered.id === e.id,
          onHover: handleEdgeHover,
          onDeselect: handleDeselect,
        };
        const dimsThroughStyle = dimmed && e.type !== 'supplyChainEdge';
        return { ...e, data, style: dimsThroughStyle ? { ...e.style, opacity: DIM_OPACITY } : e.style };
      }),
    [flowEdges, highlight, hovered, handleEdgeHover, handleDeselect]
  );

  const canvasHeight = isFullScreen ? '100%' : `${SUPPLY_GRAPH_HEIGHT}px`;

  const hasExpansions = expandedNodeIds.size > 1 || expandedOutputNodeIds.size > 1;

  return (
    <Box
      ref={containerRef}
      w="full"
      h={canvasHeight}
      bg={bgColor}
      border="none"
      borderRadius={isFullScreen ? 'none' : 'lg'}
      position="relative"
      overflow="hidden"
    >
      <ReactFlow
        nodes={displayNodes}
        edges={displayEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        zoomOnScroll
        zoomOnPinch
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        nodesFocusable={false}
        onNodeMouseEnter={(_, node) => hoverEnter('node', node.id)}
        onNodeMouseLeave={(_, node) => hoverLeave('node', node.id)}
        onPaneClick={handleDeselect}
        paneClickDistance={PANE_CLICK_DISTANCE}
        onMouseDownCapture={recordPointerDown}
        onMouseMoveCapture={trackDrag}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.05}
        proOptions={{ hideAttribution: true }}
      />

      {mode === 'impact_assessment' && <SupplyChainLciaFilters initialSelection={lciaSelection} onChange={onLciaChange} />}

      <SupplyChainLegend />

      {/* Order is load-bearing: same zIndex as the legend, so the later sibling covers it. */}
      {isInfoCardOpen && <SupplyChainInfoCard stepIndex={infoStepIndex} onNext={onInfoCardNext} onClose={onInfoCardClose} />}

      {hasExpansions && (
        <Box position="absolute" bottom={4} left="50%" transform="translateX(-50%)" zIndex={10} pointerEvents="auto">
          <Button
            size="sm"
            leftIcon={<BiCollapse size={14} />}
            bg={buttonBg}
            color={buttonColor}
            _hover={{ bg: buttonHoverBg }}
            boxShadow="sm"
            borderRadius="md"
            border={`1px solid ${buttonBorderColor}`}
            onClick={() => {
              onCollapseAll();
              requestAnimationFrame(() => fitView({ padding: 0.2, duration: 400 }));
            }}
            fontSize={'sm'}
          >
            Collapse All
          </Button>
        </Box>
      )}

      <Box ref={controlsRef} position="absolute" bottom={4} right={4} zIndex={10}>
        <GraphControls isFullScreen={isFullScreen} onToggle={onToggleFullScreen} onDownload={handleDownload} />
      </Box>
    </Box>
  );
};

export default SupplyChainCanvas;
